import { ProjectData } from '@/types';
import React, { useId } from 'react';
import { FileContextMenu } from './file-context-menu';
import { ProjectContextMenu } from './project-context-menu';

interface ProjectItemProps {
    project: ProjectData;
    selectedFile: { fileId: string; projectId: string } | null;
    selectedProject: string | null;
    onToggleExpansion: () => void;
    onProjectSelect: () => void;
    onFileSelect: (fileId: string) => void;
    onFileOpen: (fileId: string) => void;
}

export const ProjectItem: React.FC<ProjectItemProps> = React.memo(
    ({ project, selectedFile, selectedProject, onToggleExpansion, onProjectSelect, onFileSelect, onFileOpen }) => {
        const id = useId();
        const filesId = `${id}-files`;
        const folderId = `${id}-folder`;
        const files = [...project.files].sort((a, b) =>
            project.sortFiles === 'id'
                ? a.id.localeCompare(b.id)
                : project.sortFiles === 'name'
                  ? a.name.localeCompare(b.name)
                  : 0
        );
        return (
            <div id={`project-${project.id}`}>
                <ProjectContextMenu project={project}>
                    <div className='flex items-center'>
                        <button
                            type='button'
                            aria-label={`${project.isExpanded ? 'Collapse' : 'Expand'} ${project.name}`}
                            aria-expanded={project.isExpanded}
                            aria-controls={filesId}
                            onClick={onToggleExpansion}
                            className='min-w-8 min-h-8 text-vscode-text-secondary rounded file-explorer-hover'
                        >
                            <span aria-hidden='true'>{project.isExpanded ? '▼' : '▶'}</span>
                        </button>
                        <button
                            type='button'
                            id={folderId}
                            data-explorer-item
                            aria-expanded={project.isExpanded}
                            aria-controls={filesId}
                            className={`flex-1 min-h-8 px-2 text-sm text-left rounded ${selectedProject === project.id ? 'bg-[#1f6feb] text-white' : 'text-vscode-text-secondary file-explorer-hover'}`}
                            onClick={() => {
                                onProjectSelect();
                                onToggleExpansion();
                            }}
                            onKeyDown={event => {
                                if (event.key === 'ArrowRight') {
                                    event.preventDefault();
                                    if (!project.isExpanded) onToggleExpansion();
                                    else
                                        document
                                            .getElementById(filesId)
                                            ?.querySelector<HTMLButtonElement>('button[data-explorer-item]')
                                            ?.focus();
                                } else if (event.key === 'ArrowLeft' && project.isExpanded) {
                                    event.preventDefault();
                                    onToggleExpansion();
                                }
                            }}
                        >
                            {project.name}
                        </button>
                    </div>
                </ProjectContextMenu>
                <div id={filesId} hidden={!project.isExpanded} className='ml-4'>
                    {project.isExpanded &&
                        files.map(file => {
                            const isSelected =
                                selectedFile?.fileId === file.id && selectedFile.projectId === project.id;
                            return (
                                <FileContextMenu
                                    key={file.id}
                                    file={file}
                                    projectId={project.id}
                                    projectName={project.name}
                                >
                                    <button
                                        type='button'
                                        data-explorer-item
                                        id={`file-${file.id}`}
                                        aria-current={isSelected ? 'page' : undefined}
                                        className={`flex items-center w-full min-h-8 px-2 text-sm text-left rounded ${isSelected ? 'bg-[#1f6feb] text-white' : 'text-vscode-text-muted file-explorer-hover'}`}
                                        title={`${project.name}/${file.name}`}
                                        onClick={() => {
                                            onFileSelect(file.id);
                                            onFileOpen(file.id);
                                        }}
                                        onKeyDown={event => {
                                            if (event.key === 'ArrowLeft') {
                                                event.preventDefault();
                                                document.getElementById(folderId)?.focus();
                                            }
                                        }}
                                    >
                                        <span aria-hidden='true' className='mr-2'>
                                            📄
                                        </span>
                                        {file.name}
                                    </button>
                                </FileContextMenu>
                            );
                        })}
                </div>
            </div>
        );
    }
);
